import {
  faCopy,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faKey,
  faLock,
  faSearch,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import Section from 'components/common/Section';
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import api from 'util/api';
import logger from 'util/logger';

const STAGE = {
  GATE: 'gate',
  LOOKUP: 'lookup'
};

const PasswordLookup = () => {
  const [stage, setStage] = useState(STAGE.GATE);

  // Access key gate
  const [accessKey, setAccessKey] = useState('');
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [gateLoading, setGateLoading] = useState(false);

  // Access denied modal
  const [deniedOpen, setDeniedOpen] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState('');

  // Lookup
  const [email, setEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [result, setResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const showDenied = message => {
    setDeniedMessage(
      message || 'The access key you entered is not valid. Access denied.'
    );
    setDeniedOpen(true);
  };

  const isDeniedError = err =>
    err?.status === 401 ||
    err?.status === 403 ||
    err?.StatusCode === 401 ||
    err?.StatusCode === 403 ||
    err?.Code === 'Forbidden' ||
    err?.Code === 'Unauthorized';

  const isNotFoundError = err =>
    err?.status === 404 || err?.StatusCode === 404 || err?.Code === 'NotFound';

  const handleGateSubmit = e => {
    e.preventDefault();
    if (!accessKey) return;
    setGateLoading(true);
    api.AdminPasswordLookup.verifyKey(accessKey)
      .then(() => {
        setGateLoading(false);
        setStage(STAGE.LOOKUP);
      })
      .catch(err => {
        logger.log('Access key verification failed', err);
        setGateLoading(false);
        if (isDeniedError(err)) {
          showDenied();
        } else {
          showDenied(
            'Unable to verify the access key right now. Please try again.'
          );
        }
      });
  };

  const handleLookupSubmit = e => {
    e.preventDefault();
    if (!email) return;
    setLookupLoading(true);
    setLookupError('');
    setResult(null);
    setShowPassword(false);
    setCopied(false);
    api.AdminPasswordLookup.lookup(accessKey, email.trim())
      .then(res => {
        setLookupLoading(false);
        setResult(res);
      })
      .catch(err => {
        logger.log('Password lookup failed', err);
        setLookupLoading(false);
        if (isDeniedError(err)) {
          // Key was rotated / no longer valid - send them back to the gate.
          setStage(STAGE.GATE);
          setAccessKey('');
          showDenied(
            'Your access key is no longer valid. Please enter it again.'
          );
        } else if (isNotFoundError(err)) {
          setLookupError(
            `No account was found for "${email.trim()}". Check the email address and try again.`
          );
        } else {
          setLookupError(
            err?.Message ||
              'Something went wrong while retrieving the password. Please try again.'
          );
        }
      });
  };

  const copyPassword = () => {
    if (!result?.Password) return;
    try {
      navigator.clipboard.writeText(result.Password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      logger.log('Clipboard copy failed', err);
    }
  };

  const resetLookup = () => {
    setEmail('');
    setResult(null);
    setLookupError('');
    setShowPassword(false);
    setCopied(false);
  };

  return (
    <Section className="py-0">
      <Row className="flex-center min-vh-100 py-6">
        <Col sm={10} md={8} lg={7} xl={6} className="col-xxl-5">
          <Logo width={40} />

          <Card className="shadow-sm">
            <Card.Body className="p-4 p-sm-5">
              {stage === STAGE.GATE && (
                <>
                  <Flex alignItems="center" className="mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center bg-soft-primary text-primary rounded-circle me-3"
                      style={{ width: 44, height: 44 }}
                    >
                      <FontAwesomeIcon icon={faUserShield} />
                    </div>
                    <div>
                      <h5 className="mb-0">Administrator Access</h5>
                      <p className="fs--1 text-600 mb-0">
                        Enter the access key to continue
                      </p>
                    </div>
                  </Flex>

                  <Form onSubmit={handleGateSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fs--1">Access key</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faKey} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus
                          placeholder="Enter access key"
                          value={accessKey}
                          onChange={e => setAccessKey(e.target.value)}
                          type={showAccessKey ? 'text' : 'password'}
                          autoComplete="off"
                        />
                        <InputGroup.Text
                          className="cursor-pointer"
                          onClick={() => setShowAccessKey(!showAccessKey)}
                        >
                          <FontAwesomeIcon
                            icon={showAccessKey ? faEyeSlash : faEye}
                          />
                        </InputGroup.Text>
                      </InputGroup>
                    </Form.Group>

                    <Button
                      type="submit"
                      color="primary"
                      className="w-100"
                      disabled={!accessKey || gateLoading}
                    >
                      {gateLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faLock} className="me-2" />
                          Unlock
                        </>
                      )}
                    </Button>
                  </Form>
                </>
              )}

              {stage === STAGE.LOOKUP && (
                <>
                  <Flex
                    justifyContent="between"
                    alignItems="center"
                    className="mb-2"
                  >
                    <h5 className="mb-0">Retrieve User Password</h5>
                    <span className="badge bg-soft-success text-success">
                      <FontAwesomeIcon icon={faLock} className="me-1" />
                      Unlocked
                    </span>
                  </Flex>
                  <p className="fs--1 text-600">
                    Enter the user&apos;s email address to retrieve their
                    password.
                  </p>

                  <Alert variant="warning" className="fs--1 py-2 px-3">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-2"
                    />
                    This tool displays a live account credential. Only use it
                    for authorized troubleshooting.
                  </Alert>

                  <Form onSubmit={handleLookupSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fs--1">Email address</Form.Label>
                      <Form.Control
                        autoFocus
                        placeholder="user@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        type="email"
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      color="primary"
                      className="w-100"
                      disabled={!email || lookupLoading}
                    >
                      {lookupLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Retrieving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                          Retrieve Password
                        </>
                      )}
                    </Button>
                  </Form>

                  {lookupError && (
                    <Alert
                      variant="danger"
                      className="fs--1 mt-3 mb-0 py-2 px-3"
                    >
                      {lookupError}
                    </Alert>
                  )}

                  {result && (
                    <Card className="mt-4 border">
                      <Card.Body className="p-3">
                        <h6 className="fs--1 text-700 text-uppercase mb-3">
                          Account found
                        </h6>

                        <Row className="mb-2">
                          <Col xs={4} className="fs--1 text-600">
                            Name
                          </Col>
                          <Col xs={8} className="fs--1 fw-semi-bold">
                            {[result.FirstName, result.LastName]
                              .filter(Boolean)
                              .join(' ') || '—'}
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={4} className="fs--1 text-600">
                            Email
                          </Col>
                          <Col xs={8} className="fs--1 fw-semi-bold text-break">
                            {result.Email || email.trim()}
                          </Col>
                        </Row>
                        <Row className="align-items-center">
                          <Col xs={4} className="fs--1 text-600">
                            Password
                          </Col>
                          <Col xs={8}>
                            <InputGroup>
                              <Form.Control
                                readOnly
                                value={result.Password || ''}
                                type={showPassword ? 'text' : 'password'}
                                className="fw-semi-bold"
                              />
                              <InputGroup.Text
                                className="cursor-pointer"
                                onClick={() => setShowPassword(!showPassword)}
                                title={showPassword ? 'Hide' : 'Show'}
                              >
                                <FontAwesomeIcon
                                  icon={showPassword ? faEyeSlash : faEye}
                                />
                              </InputGroup.Text>
                              <InputGroup.Text
                                className="cursor-pointer"
                                onClick={copyPassword}
                                title="Copy"
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </InputGroup.Text>
                            </InputGroup>
                            {copied && (
                              <span className="fs--2 text-success">
                                Copied to clipboard
                              </span>
                            )}
                          </Col>
                        </Row>

                        <div className="text-end mt-3">
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 fs--1"
                            onClick={resetLookup}
                          >
                            Look up another account
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Access denied modal */}
      <Modal
        show={deniedOpen}
        onHide={() => setDeniedOpen(false)}
        centered
        contentClassName="border-0"
      >
        <Modal.Body className="p-0">
          <div className="bg-danger text-white text-center py-4 rounded-top">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
            <h5 className="text-white mt-3 mb-0">Access Denied</h5>
          </div>
          <div className="p-4 text-center">
            <p className="mb-4 text-700">{deniedMessage}</p>
            <Button
              variant="danger"
              className="px-5"
              onClick={() => setDeniedOpen(false)}
            >
              Dismiss
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Section>
  );
};

export default PasswordLookup;
